// src/api/rfidAPI.js
import apiClient from './client';

const RFID_CARDS_URL = '/api/v1/rfid_cards/';
const COMPANY_RFID_CARDS_URL = '/api/v1/companies/';
const DRIVER_RFID_CARDS_URL = '/api/v1/drivers/';

// Default form model for RFID card creation
export const defaultRFIDCardFormValues = {
  RFIDCardId: '',
  RFIDCardCompanyId: 0,
  RFIDCardDriverId: 0,
  RFIDCardNameOn: '',
  RFIDCardNumberOn: '',
  RFIDCardEnabled: true,
  RFIDCardExpiration: null
};

// Default display model for RFID cards
export const defaultRFIDCardValues = {
  RFIDCardId: '',
  RFIDCardCompanyId: 0,
  RFIDCardDriverId: 0,
  RFIDCardNameOn: '',
  RFIDCardNumberOn: '',
  RFIDCardEnabled: true,
  RFIDCardExpiration: null,
  RFIDCardCreated: '',
  RFIDCardUpdated: ''
};

// Get all RFID cards with optional filtering
export const getRFIDCards = async (companyId, driverId, enabled) => {
  try {
    const params = {};
    if (companyId !== undefined) params.company_id = companyId;
    if (driverId !== undefined) params.driver_id = driverId;
    if (enabled !== undefined) params.enabled = enabled;

    const response = await apiClient.get(RFID_CARDS_URL, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching RFID cards:', error);
    throw error;
  }
};

// Get all RFID cards for a specific company
export const getCompanyRFIDCards = async (companyId, driverId, enabled) => {
  try {
    const params = {};
    if (driverId !== undefined) params.driver_id = driverId;
    if (enabled !== undefined) params.enabled = enabled;
    
    const response = await apiClient.get(`${COMPANY_RFID_CARDS_URL}${companyId}/rfid_cards`, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching RFID cards for company ${companyId}:`, error);
    throw error;
  }
};

// Get all RFID cards for a specific driver
export const getDriverRFIDCards = async (driverId, companyId) => {
  try {
    const params = {};
    if (companyId !== undefined) params.company_id = companyId;
    
    const response = await apiClient.get(`${DRIVER_RFID_CARDS_URL}${driverId}/rfid_cards`, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching RFID cards for driver ${driverId}:`, error);
    throw error;
  }
};

// Get a single RFID card by ID
export const getRFIDCard = async (rfidCardId) => {
  try {
    const response = await apiClient.get(`${RFID_CARDS_URL}${rfidCardId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching RFID card ${rfidCardId}:`, error);
    throw error;
  }
};

// Create a new RFID card
export const createRFIDCard = async (rfidCardData) => {
  try {
    const response = await apiClient.post(RFID_CARDS_URL, rfidCardData);
    return response.data;
  } catch (error) {
    console.error('Error creating RFID card:', error);
    throw error;
  }
};

// Update an RFID card
export const updateRFIDCard = async (rfidCardId, rfidCardData) => {
  try {
    const response = await apiClient.put(`${RFID_CARDS_URL}${rfidCardId}`, rfidCardData);
    return response.data;
  } catch (error) {
    console.error(`Error updating RFID card ${rfidCardId}:`, error);
    throw error;
  }
};

// Delete an RFID card
export const deleteRFIDCard = async (rfidCardId) => {
  try {
    await apiClient.delete(`${RFID_CARDS_URL}${rfidCardId}`);
  } catch (error) {
    console.error(`Error deleting RFID card ${rfidCardId}:`, error);
    throw error;
  }
};

// Helper function to generate RFID card ID (can be used in forms)
export const generateRFIDCardId = (companyId, driverId) => {
  const timestamp = Date.now().toString().slice(-6);
  return `${companyId}${driverId}${timestamp}`;
};

// Helper function to validate RFID card data
export const validateRFIDCardData = (rfidCardData) => {
  const errors = {};
  
  if (!rfidCardData.RFIDCardId || rfidCardData.RFIDCardId.trim() === '') {
    errors.RFIDCardId = 'RFID Card ID is required';
  }
  
  if (!rfidCardData.RFIDCardCompanyId || rfidCardData.RFIDCardCompanyId <= 0) {
    errors.RFIDCardCompanyId = 'Valid Company ID is required';
  }
  
  if (!rfidCardData.RFIDCardDriverId || rfidCardData.RFIDCardDriverId <= 0) {
    errors.RFIDCardDriverId = 'Valid Driver ID is required';
  }
  
  if (!rfidCardData.RFIDCardNameOn || rfidCardData.RFIDCardNameOn.trim() === '') {
    errors.RFIDCardNameOn = 'Cardholder name is required';
  }
  
  if (!rfidCardData.RFIDCardNumberOn || rfidCardData.RFIDCardNumberOn.trim() === '') {
    errors.RFIDCardNumberOn = 'Card number is required';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};